/**
 * FileManagerDialog — "Load map" dialog for the Synth VR editor.
 *
 * Mirrors the Flow editor's Load dialog (same DialogShell chrome + "Connect a
 * folder" / "Upload…" footer), differing only in the file type (.synth instead
 * of .flow). Because .synth files are parsed by the Python backend, "Connect a
 * folder" reads the folder's .synth files via the File System Access API and
 * uploads them to the backend; "Upload…" uploads picked files. The body lists
 * the backend's loaded library, click to open.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { uploadFiles, listBeatmaps, getBeatmap, deleteBeatmap, getArtworkUrl, getAudioUrl, revealBeatmap, setBeatmapVisibility } from '../../api/client';
import type { BeatmapSummary } from '../../types/beatmap';
import { color } from '../ui/theme';
import { IcFolder, IcUpload, IcRefresh, IcSearch } from '../ui/icons';
import { DialogShell } from '../../editors/flow/FlowDialogShell';
import MapListRow from '../ui/MapListRow';
import { useSongPreview } from '../ui/useSongPreview';
import Spinner from '../ui/Spinner';

const SYNTH_PASSWORD = 'hC2*wE5R*qQzv@a!';
const FS_SUPPORTED = typeof window !== 'undefined' && 'showDirectoryPicker' in window;

// Recently-opened maps — a small MRU list persisted per editor so the user can
// re-open the last map(s) they had loaded without searching the whole library.
const RECENT_CAP = 3;
const recentKey = (editor: string) => `synapse_recent_opened_${editor}`;
function readRecent(editor: string): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(recentKey(editor)) || '[]');
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}
function writeRecent(editor: string, ids: string[]): void {
  try { localStorage.setItem(recentKey(editor), JSON.stringify(ids.slice(0, RECENT_CAP))); } catch { /* ignore quota */ }
}

/** Multi-keyword search: every whitespace-separated token must appear somewhere
 *  in the text (substring, anywhere — not just the first word / a prefix). */
function matchesQuery(text: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = text.toLowerCase();
  return q.split(/\s+/).every(tok => hay.includes(tok));
}

export default function FileManagerDialog() {
  const fileManagerOpen = useEditorStore(s => s.fileManagerOpen);
  const setFileManagerOpen = useEditorStore(s => s.setFileManagerOpen);
  const loadBeatmap = useEditorStore(s => s.loadBeatmap);
  const beatmap = useEditorStore(s => s.beatmap);
  const mapLocked = useEditorStore(s => s.mapLocked);
  // Each editor lists/uploads only ITS OWN library ('synth-rider' | 'console')
  // so Synth VR and Synth Console don't show each other's maps.
  const activeView = useEditorStore(s => s.activeView);

  const [beatmaps, setBeatmaps] = useState<BeatmapSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folderName, setFolderName] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'mine' | 'public'>('mine');
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load the persisted MRU list when the dialog opens (per editor).
  useEffect(() => {
    if (fileManagerOpen) setRecentIds(readRecent(activeView));
  }, [fileManagerOpen, activeView]);

  // Record the currently-loaded map as most-recently-opened. Watching the loaded
  // `beatmap` captures EVERY open path (this dialog, boot restore, scene
  // double-click), not just picks made here.
  useEffect(() => {
    const id = beatmap?.id;
    if (!id) return;
    setRecentIds(prev => {
      if (prev[0] === id) return prev;
      const next = [id, ...prev.filter(x => x !== id)].slice(0, RECENT_CAP);
      writeRecent(activeView, next);
      return next;
    });
  }, [beatmap?.id, activeView]);

  // Song preview (the per-row play button) — shared hook owns one <audio>.
  const { previewId, toggle: togglePreview, stop: stopPreview } = useSongPreview();
  // Stop the preview when the dialog closes.
  useEffect(() => {
    if (!fileManagerOpen) stopPreview();
  }, [fileManagerOpen, stopPreview]);

  const refreshList = useCallback(async () => {
    try { setBeatmaps(await listBeatmaps(activeView)); } catch { /* backend offline — keep current */ }
  }, [activeView]);

  // Refresh on open + auto-fill the password when the map is locked.
  useEffect(() => {
    if (fileManagerOpen) {
      void refreshList();
      if (mapLocked) setPassword(SYNTH_PASSWORD);
    }
  }, [fileManagerOpen, refreshList, mapLocked]);

  // Poll while open — the backend auto-loads .synth files in a background thread.
  useEffect(() => {
    if (!fileManagerOpen) return;
    const id = window.setInterval(() => void refreshList(), 2500);
    return () => window.clearInterval(id);
  }, [fileManagerOpen, refreshList]);

  const doUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setBusy(true); setError(null); setNeedsPassword(false);
    try {
      // Upload in batches so a big library (many large .synth files) doesn't go
      // out as one huge request that can time out / be rejected — and so partial
      // progress persists (the list refreshes after each batch).
      const BATCH = 15;
      const errs: string[] = [];
      let needsPw = false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let firstBeatmap: any = null;
      for (let i = 0; i < files.length; i += BATCH) {
        const chunk = files.slice(i, i + BATCH);
        const result = await uploadFiles(chunk, password || undefined, activeView);
        for (const r of result.results) {
          if (!r.success) {
            if (r.error) errs.push(r.error);
            if (r.message?.toLowerCase().includes('encrypted')) needsPw = true;
          } else if (!firstBeatmap && r.beatmap) {
            firstBeatmap = r.beatmap;
          }
        }
        await refreshList(); // progressive — maps appear as each batch lands
      }
      if (errs.length) {
        setError(errs.slice(0, 3).join('; ') + (errs.length > 3 ? ` (+${errs.length - 3} more)` : ''));
      }
      if (needsPw) setNeedsPassword(true);
      if (firstBeatmap) loadBeatmap(firstBeatmap);
      await refreshList();
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }, [password, loadBeatmap, refreshList, activeView]);

  // Pick a folder on the PC, read its .synth files, upload them to the backend.
  const handleConnectFolder = useCallback(async () => {
    try {
      const dir = await (window as any).showDirectoryPicker();
      setFolderName(dir.name);
      setBusy(true); setError(null);
      const files: File[] = [];
      // Recurse into subfolders too — maps are often organised in nested folders.
      // Resilient: a single unreadable subfolder/entry is skipped, not fatal, so
      // one bad folder can't abort the whole scan (which would leave only the
      // previously-loaded maps showing).
      const walk = async (handle: any, depth: number): Promise<void> => {
        if (depth > 12) return; // safety cap against pathological trees
        try {
          for await (const entry of handle.values()) {
            try {
              if (entry.kind === 'file' && entry.name.toLowerCase().endsWith('.synth')) {
                files.push(await entry.getFile());
              } else if (entry.kind === 'directory') {
                await walk(entry, depth + 1);
              }
            } catch { /* skip an entry we can't read */ }
          }
        } catch { /* skip a directory we can't enumerate */ }
      };
      await walk(dir, 0);
      if (files.length === 0) { setError('No .synth files in this folder (or its subfolders).'); setBusy(false); return; }
      await doUpload(files);
    } catch (e: any) {
      if (e?.name !== 'AbortError') setError(e?.message || 'Could not read the folder');
      setBusy(false);
    }
  }, [doUpload]);

  const handleSelect = useCallback(async (id: string) => {
    try { loadBeatmap(await getBeatmap(id)); setFileManagerOpen(false); }
    catch (e: any) { setError(e?.message || 'Failed to load'); }
  }, [loadBeatmap, setFileManagerOpen]);

  const handleDelete = useCallback(async (id: string) => {
    try { await deleteBeatmap(id); await refreshList(); }
    catch (e: any) { setError(e?.message || 'Delete failed'); }
  }, [refreshList]);

  const handleToggleVisibility = useCallback(async (id: string, makePublic: boolean) => {
    try { await setBeatmapVisibility(id, makePublic); await refreshList(); }
    catch (e: any) { setError(e?.message || 'Could not change visibility'); }
  }, [refreshList]);

  const handleOpenFolder = useCallback(async (id: string) => {
    setError(null);
    try { await revealBeatmap(id); }
    catch (e: any) { setError(e?.message || 'Could not open the folder'); }
  }, []);

  if (!fileManagerOpen) return null;

  // Resolve the MRU ids against the loaded library (skips maps not yet scanned
  // or deleted) so the section always has full metadata + cover.
  const recentMaps = recentIds
    .map(id => beatmaps.find(bm => bm.id === id))
    .filter((bm): bm is BeatmapSummary => !!bm);

  return (
    <DialogShell
      title="Open Beatmap"
      subtitle="Open a map from your PC, or upload a .synth file."
      onClose={() => setFileManagerOpen(false)}
      width={520}
      busy={busy}
      footer={
        <>
          {FS_SUPPORTED && (
            <button
              onClick={handleConnectFolder}
              style={footerBtnStyle}
              title="Pick a folder of .synth files on your PC"
            >
              {folderName
                ? <><IcRefresh size={14} /> Change folder</>
                : <><IcFolder size={14} /> Connect a folder</>}
            </button>
          )}
          <button onClick={() => fileInputRef.current?.click()} style={footerBtnStyle} title="Import a .synth file">
            <IcUpload size={14} /> Upload…
          </button>
        </>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".synth,.zip"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => { void doUpload(Array.from(e.target.files || [])); e.target.value = ''; }}
      />

      {needsPassword && (
        <input
          type="password"
          placeholder="Password (encrypted map)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={pwInputStyle}
        />
      )}
      {error && <div style={errorStyle}>{error}</div>}

      {recentMaps.length > 0 && (
        <section style={sectionStyle}>
          <div style={sectionHeadStyle}>
            <span style={sectionTitleStyle}>Recently opened</span>
          </div>
          <ul style={listStyle}>
            {recentMaps.map(bm => (
              <MapListRow
                key={`recent-${bm.id}`}
                name={bm.name}
                artist={bm.author}
                mapper={bm.beatmapper}
                bpm={bm.bpm}
                diffCount={Object.keys(bm.difficulties || {}).length}
                cover={getArtworkUrl(bm.id)}
                active={beatmap?.id === bm.id}
                previewing={previewId === bm.id}
                isPublic={bm.visibility === 'public'}
                onPick={() => handleSelect(bm.id)}
                onTogglePreview={() => togglePreview(bm.id, getAudioUrl(bm.id))}
                onOpenFolder={bm.source_path ? () => handleOpenFolder(bm.id) : undefined}
                onToggleVisibility={bm.is_owner ? () => handleToggleVisibility(bm.id, bm.visibility !== 'public') : undefined}
                onDelete={bm.is_owner ? () => handleDelete(bm.id) : undefined}
              />
            ))}
          </ul>
        </section>
      )}

      {beatmaps.length > 0 ? (
        <section style={sectionStyle}>
          <div style={sectionHeadStyle}>
            <span style={sectionTitleStyle}>Library ({beatmaps.length})</span>
            <button onClick={() => void refreshList()} style={refreshBtnStyle} title="Refresh">↻</button>
          </div>
          {folderName && (
            <div style={folderChipStyle} title={folderName}>
              <IcFolder size={15} />
              <span style={folderChipNameStyle}>{folderName}</span>
            </div>
          )}
          {/* Mine / Public scope filter. */}
          <div style={scopeRowStyle}>
            {(['mine', 'public'] as const).map(s => (
              <button
                key={s}
                onClick={() => setScope(s)}
                style={{ ...scopeBtnStyle, ...(scope === s ? scopeBtnActiveStyle : null) }}
              >
                {s === 'mine' ? 'Mine' : 'Public'}
              </button>
            ))}
          </div>
          {/* Search — matches any keyword, anywhere in name / artist / mapper. */}
          <div style={searchWrapStyle}>
            <span style={searchIconStyle}><IcSearch size={14} /></span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, artist, or mapper…"
              style={searchInputStyle}
              spellCheck={false}
            />
          </div>
          <div style={listWrapStyle}>
            {(() => {
              const filtered = beatmaps.filter(bm =>
                (scope === 'mine' ? bm.is_owner : (!bm.is_owner && bm.visibility === 'public')) &&
                matchesQuery(`${bm.name} ${bm.author || ''} ${bm.beatmapper || ''}`, query));
              if (filtered.length === 0) {
                return <div style={emptyRowStyle}>{query ? `No maps match “${query}”.` : 'No maps here yet.'}</div>;
              }
              return (
                <ul style={listStyle}>
                  {filtered.map(bm => (
                    <MapListRow
                      key={bm.id}
                      name={bm.name}
                      artist={bm.author}
                      mapper={bm.beatmapper}
                      bpm={bm.bpm}
                      diffCount={Object.keys(bm.difficulties || {}).length}
                      cover={getArtworkUrl(bm.id)}
                      active={beatmap?.id === bm.id}
                      previewing={previewId === bm.id}
                      isPublic={bm.visibility === 'public'}
                      onPick={() => handleSelect(bm.id)}
                      onTogglePreview={() => togglePreview(bm.id, getAudioUrl(bm.id))}
                      onOpenFolder={bm.source_path ? () => handleOpenFolder(bm.id) : undefined}
                      onToggleVisibility={bm.is_owner ? () => handleToggleVisibility(bm.id, bm.visibility !== 'public') : undefined}
                      onDelete={bm.is_owner ? () => handleDelete(bm.id) : undefined}
                    />
                  ))}
                </ul>
              );
            })()}
          </div>
        </section>
      ) : (
        <div style={emptyBodyStyle}>
          {busy
            ? <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}><Spinner size={40} label="Loading library…" /></div>
            : FS_SUPPORTED
              ? 'Connect a folder on your PC — or upload a .synth file — to load a map.'
              : 'Upload a .synth file to load a map.'}
        </div>
      )}
    </DialogShell>
  );
}

// ── Styles (mirror the Flow Load dialog) ──────────────────────────────────────

const footerBtnStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '9px 12px',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.10)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  borderRadius: 8,
  color: color.ink,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: 0.4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const sectionStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 };
const sectionHeadStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const sectionTitleStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: color.inkDim,
};
const refreshBtnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.inkDim, borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 13,
};

const folderChipStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px', borderRadius: 8,
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.14)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.3)',
  color: '#F5E8FF', fontSize: 12, fontWeight: 600,
};
const folderChipNameStyle: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// Tall enough to show ~5 cover rows before scrolling.
const listWrapStyle: React.CSSProperties = { maxHeight: 430, overflowY: 'auto' };
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 };
// Row markup + styles now live in the shared <MapListRow>.

const emptyBodyStyle: React.CSSProperties = {
  padding: '34px 16px', textAlign: 'center', color: '#7A7099', fontSize: 12, lineHeight: 1.5,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)', borderRadius: 8,
  background: 'rgba(8, 5, 20, 0.6)', marginTop: 12,
};

const pwInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', marginTop: 12, borderRadius: 8,
  background: 'rgba(15, 15, 30, 0.5)', border: '1px solid rgba(251, 191, 36, 0.3)',
  color: color.ink, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

const errorStyle: React.CSSProperties = {
  color: '#F87171', fontSize: 11, padding: '6px 10px', marginTop: 12,
  background: 'rgba(248, 113, 113, 0.08)', border: '1px solid rgba(248, 113, 113, 0.15)', borderRadius: 8,
};

const searchWrapStyle: React.CSSProperties = { position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 6 };
const searchIconStyle: React.CSSProperties = {
  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
  color: color.inkFaint, display: 'flex', pointerEvents: 'none',
};
const searchInputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  color: color.ink, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};
const emptyRowStyle: React.CSSProperties = {
  padding: '24px 12px', textAlign: 'center', color: color.inkFaint, fontSize: 11.5,
};

const scopeRowStyle: React.CSSProperties = { display: 'flex', gap: 6, marginBottom: 6 };
const scopeBtnStyle: React.CSSProperties = {
  flex: 1, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: color.inkDim,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
};
const scopeBtnActiveStyle: React.CSSProperties = {
  color: '#F5E8FF',
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)',
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.45)',
};
